import { getMarketSpecs } from '@shared/config/market/market.helpers';
import { Markets } from '@shared/config/market/market.models';
import {
    CombinationIdType,
    DevCancellationType,
    DevPanelPage,
    FakeData,
    FakeDataRyanairOption,
} from './DevPanel.interfaces';
import { MyBookingStatus, ProductType } from '@/api/MyBooking/myBooking.interfaces';
import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
import { BusinessArea, SalesChannel } from '@/api/Cart/cart.interfaces';
import { LoanType } from '@/api/LeaBank/leaBank.interfaces';
import { isObject, pruneObjectKeys } from '@shared/utils/object';
import merge from 'lodash/merge';

const FAKE_DATA_KEY = 'fake-data';

const initialFakeDataStored: FakeData = {
    enabled: false,
    log: true,
    products: [],
    auth: {
        enabled: false,
        member: {
            canBeSembonusUser: false,
            isSembonusUser: false,
        },
    },
    chatWidget: {
        enabled: false,
    },
    checkout: {
        version: 'v3',
        search: {
            enabled: false,
            adults: {
                min: 1,
                max: 1,
            },
            children: {
                min: 1,
                max: 1,
            },
            infants: {
                min: 1,
                max: 1,
            },
        },
        loan: {
            enabled: false,
            type: LoanType.Basic,
        },
        affiliate: {
            enabled: false,
            affiliateId: '',
            salesChannel: SalesChannel.Unknown,
            businessArea: BusinessArea.Unknown,
            groupName: '',
        },
        bookingSalesChannels: ['default', 'planner'],
        hasAgency: false,
        hasAddonCancellation: true,
        hasAddonLuggage: true,
        hasAddonServicePackage: true,
        hasBonusNights: false,
        hasAddonTravelInsurance: false,
        hasPaymentDiscount: false,
        hasCampaignDiscount: false,
        hasAmountAlreadyPaid: false,
        combinationIdType: CombinationIdType.None,
        cancellationType: DevCancellationType.Random,
    },
    booking: {
        status: MyBookingStatus.Completed,
        createdToday: false,
        canDisplayPaymentReminder: false,
        hasServicePackage: false,
        servicePackageSelection: undefined,
        hasCustomerServiceMessages: false,
        hasImportantInformation: false,
        hasLeaBankPayment: false,
        hasDownPayment: false,
        hasFinalPayment: false,
        hasCloseFinalPayment: true,
        hasAmountAlreadyPaid: false,
        ryanair: {
            enabled: false,
            hasUrl: FakeDataRyanairOption.Some,
            isConfirmed: FakeDataRyanairOption.Some,
            isUrlValid: FakeDataRyanairOption.Some,
        },
    },
    bookingSession: {
        hasServicePackage: false,
        hasServicePackageBasic: false,
        hasServicePackagePremium: false,
    },
    bookings: {
        isAuthenticated: true,
        isVerified: true,
        count: 10,
    },
};

export function getCombinationTypeOptions(): SelectItem[] {
    return Object.values(CombinationIdType).map((x) => {
        return {
            value: x,
            label: CombinationIdType[x],
        };
    });
}

export function getCancellationTypeOptions(): SelectItem[] {
    return Object.values(DevCancellationType).map((x) => {
        return {
            value: x,
            label: DevCancellationType[x],
        };
    });
}

export function getLoanTypesOptions(): SelectItem[] {
    return Object.values(LoanType).map((x) => {
        return {
            value: x,
            label: LoanType[x],
        };
    });
}

export function getSalesChannelsOptions(): SelectItem[] {
    return Object.values(SalesChannel).map((x) => {
        return {
            value: x,
            label: SalesChannel[x],
        };
    });
}

export function getBusinessAreaOptions(): SelectItem[] {
    return Object.values(BusinessArea).map((x) => {
        return {
            value: x,
            label: BusinessArea[x],
        };
    });
}

export function getMarketsOptions(): SelectItem[] {
    return Object.keys(Markets)
        .filter((key) => isNaN(Number(key)))
        .map((key) => {
            const marketKey = key as keyof typeof Markets;
            const market = getMarketSpecs(Markets[marketKey]);

            return {
                value: market.number,
                label: `
                ${market.locale.toUpperCase()} - ${market.domain} 
                (${market.number})
                `,
            };
        })
        .sort((a, b) => a.value - b.value);
}

export function getMyBookingRyanairOptions(): SelectItem[] {
    return Object.values(FakeDataRyanairOption).map((x) => {
        return {
            value: x,
            label: FakeDataRyanairOption[x],
        };
    });
}

export function getMyBookingServicePackageOptions(): SelectItem[] {
    return [
        {
            value: ProductType.ServicePackageOld,
            label: 'Old service',
        },
        {
            value: ProductType.ServicePackageBasic,
            label: 'Basic',
        },
        {
            value: ProductType.ServicePackagePremium,
            label: 'Premium',
        },
    ];
}

export function getMyBookingStatuses(): SelectItem[] {
    return Object.values(MyBookingStatus).map((x) => {
        return {
            value: x,
            label: MyBookingStatus[x],
        };
    });
}

export function getStoredFakeData(): FakeData {
    const stored = window.localStorage.getItem(FAKE_DATA_KEY);

    if (!stored) return { ...initialFakeDataStored };

    try {
        const parsed = JSON.parse(stored);

        if (!isObject(parsed)) {
            console.warn('[FakeData] Stored data is invalid. Resetting to defaults.');
            window.localStorage.removeItem(FAKE_DATA_KEY);
            return { ...initialFakeDataStored };
        }

        const merged: FakeData = merge({}, initialFakeDataStored, parsed);
        const cleaned: FakeData = pruneObjectKeys(initialFakeDataStored, merged);

        window.localStorage.setItem(FAKE_DATA_KEY, JSON.stringify(cleaned));

        return cleaned;
    } catch (error) {
        console.error('[FakeData] Failed to parse stored data:', error);
        window.localStorage.removeItem(FAKE_DATA_KEY);
        return { ...initialFakeDataStored };
    }
}

export function setStoredFakeData(filter: FakeData | null) {
    if (filter) {
        window.localStorage.setItem(FAKE_DATA_KEY, JSON.stringify(filter));
    } else {
        window.localStorage.removeItem(FAKE_DATA_KEY);
    }
}

export function isUsingFakeData() {
    const stored = getStoredFakeData();
    return stored.enabled;
}

export function getMessagesByPage(page: DevPanelPage): string[] {
    if (page === DevPanelPage.Booking) {
        return [];
    }

    return [];
}
